import { useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card, Dato, Seccion } from '@/components/Card';
import { leerSettings, actualizarSettings, totalRegistrosNegocio } from '@/db/schema';
import {
  exportarRespaldo,
  importarRespaldo,
  analizarRespaldo,
  serializarRespaldo,
  nombreArchivoRespaldo,
  type RespaldoAnalizado,
} from '@/db/backup';
import { describirUltimoRespaldo } from '@/domain/backup-reminder';
import { ahoraIso } from '@/domain/dates';
import { mensajeDeError } from '@/db/tx';
import { ExportarAnalisis } from './ExportarAnalisis';
import {
  descargarArchivo,
  compartirArchivo,
  puedeCompartirArchivos,
  leerArchivoComoTexto,
  copiarAlPortapapeles,
} from '@/lib/file-transfer';

type EstadoImportacion =
  | { fase: 'inactivo' }
  | { fase: 'analizando' }
  | { fase: 'confirmando'; analizado: RespaldoAnalizado; nombreArchivo: string }
  | { fase: 'importando' }
  | { fase: 'listo'; registros: number }
  | { fase: 'error'; mensaje: string };

export function BackupPage() {
  const settings = useLiveQuery(() => leerSettings(), []);
  const totalRegistros = useLiveQuery(() => totalRegistrosNegocio(), [], 0);

  const [exportando, setExportando] = useState(false);
  const [mensajeExporta, setMensajeExporta] = useState<string | null>(null);
  const [errorExporta, setErrorExporta] = useState<string | null>(null);
  const [textoRespaldo, setTextoRespaldo] = useState<string | null>(null);

  const [importacion, setImportacion] = useState<EstadoImportacion>({ fase: 'inactivo' });
  const [forzarImportacion, setForzarImportacion] = useState(false);
  const entradaArchivo = useRef<HTMLInputElement>(null);

  const puedeCompartir = puedeCompartirArchivos();

  /** Genera el respaldo y registra la fecha solo si la entrega tuvo exito. */
  async function generar(via: 'descarga' | 'compartir' | 'texto') {
    setExportando(true);
    setErrorExporta(null);
    setMensajeExporta(null);
    setTextoRespaldo(null);

    try {
      const archivo = await exportarRespaldo();
      const contenido = serializarRespaldo(archivo);
      const nombre = nombreArchivoRespaldo();

      if (via === 'texto') {
        setTextoRespaldo(contenido);
        const copiado = await copiarAlPortapapeles(contenido);
        setMensajeExporta(
          copiado
            ? 'Contenido copiado al portapapeles. Pegalo en una nota o correo y guardalo.'
            : 'Selecciona todo el texto de abajo y copialo a una nota o correo.',
        );
        // No se marca como respaldo hecho: aun no se guardo en ningun lado.
        return;
      }

      if (via === 'compartir') {
        const compartido = await compartirArchivo(nombre, contenido);
        if (!compartido) {
          setErrorExporta(
            'No se pudo compartir el archivo. Usa "Descargar respaldo", que es la via mas confiable en iPhone.',
          );
          return;
        }
      } else {
        descargarArchivo(nombre, contenido);
      }

      await actualizarSettings({ ultimoRespaldoEn: ahoraIso() });
      setMensajeExporta(
        via === 'compartir'
          ? `Respaldo compartido: ${nombre}`
          : `Respaldo descargado: ${nombre}. Revisalo en la app Archivos.`,
      );
    } catch (error) {
      setErrorExporta(mensajeDeError(error));
    } finally {
      setExportando(false);
    }
  }

  /** Paso 1 al 6 de la importacion: analizar sin tocar ningun dato. */
  async function seleccionarArchivo(archivo: File) {
    setImportacion({ fase: 'analizando' });
    setForzarImportacion(false);

    try {
      const texto = await leerArchivoComoTexto(archivo);
      const analizado = await analizarRespaldo(texto);
      setImportacion({ fase: 'confirmando', analizado, nombreArchivo: archivo.name });
    } catch (error) {
      setImportacion({ fase: 'error', mensaje: mensajeDeError(error) });
    }
  }

  /** Paso 7 al 8: respaldo automatico de seguridad y reemplazo. */
  async function confirmarImportacion(analizado: RespaldoAnalizado) {
    setImportacion({ fase: 'importando' });

    try {
      // Req. 3.8: red de seguridad antes de reemplazar.
      const actual = await exportarRespaldo();
      descargarArchivo(
        `ANTES-DE-IMPORTAR-${nombreArchivoRespaldo()}`,
        serializarRespaldo(actual),
      );

      await importarRespaldo(analizado.archivo);
      setImportacion({ fase: 'listo', registros: analizado.resumen.totalRegistros });
    } catch (error) {
      setImportacion({ fase: 'error', mensaje: mensajeDeError(error) });
    }
  }

  function reiniciarImportacion() {
    setImportacion({ fase: 'inactivo' });
    setForzarImportacion(false);
    if (entradaArchivo.current !== null) entradaArchivo.current.value = '';
  }

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Respaldos"
        descripcion="Tus datos viven solo en este telefono. El respaldo es la unica forma de recuperarlos."
      />

      <Card>
        <dl className="divide-y divide-borde">
          <Dato
            etiqueta="Ultimo respaldo"
            valor={describirUltimoRespaldo(settings?.ultimoRespaldoEn ?? null)}
          />
          <Dato etiqueta="Registros guardados" valor={String(totalRegistros ?? 0)} />
        </dl>
      </Card>

      {/* ------------------------------------- Respaldo completo (JSON) */}
      <Seccion
        titulo="Respaldo completo"
        descripcion="Archivo JSON con toda tu informacion. Es el unico que sirve para restaurar."
      >
        <Banner tono="exito" titulo="Este es tu respaldo oficial">
          Contiene todas tus tablas completas: insumos, compras, inventario, recetas, menu,
          produccion, ventas, pedidos, configuracion e historial. Con este archivo se recupera
          todo.
        </Banner>

        <div className="space-y-2">
          <Button
            tamano="grande"
            disabled={exportando}
            onClick={() => {
              void generar('descarga');
            }}
          >
            {exportando ? 'Generando…' : '💾 Descargar respaldo'}
          </Button>

          {puedeCompartir && (
            <Button
              variante="secundario"
              disabled={exportando}
              onClick={() => {
                void generar('compartir');
              }}
            >
              📤 Compartir respaldo
            </Button>
          )}

          <Button
            variante="fantasma"
            disabled={exportando}
            onClick={() => {
              void generar('texto');
            }}
          >
            Ver el contenido como texto
          </Button>
        </div>

        {mensajeExporta !== null && (
          <Banner tono="exito" titulo="Respaldo generado">
            {mensajeExporta}
          </Banner>
        )}

        {errorExporta !== null && (
          <Banner tono="alerta" titulo="No se pudo generar el respaldo">
            {errorExporta}
          </Banner>
        )}

        {textoRespaldo !== null && (
          <div className="space-y-2">
            <label htmlFor="texto-respaldo" className="block text-sm font-medium text-texto">
              Contenido del respaldo
            </label>
            <textarea
              id="texto-respaldo"
              readOnly
              value={textoRespaldo}
              onFocus={(evento) => evento.currentTarget.select()}
              className="h-48 w-full rounded-xl border border-borde bg-superficie p-3 font-mono text-xs text-texto"
            />
            <p className="text-xs text-texto-tenue">
              Esta via no cuenta como respaldo hecho hasta que guardes el texto en algun
              lugar seguro.
            </p>
          </div>
        )}
      </Seccion>

      {/* --------------------------- Exportacion para analisis (Excel) */}
      <ExportarAnalisis />

      {/* ------------------------------------------------ Importar */}
      <Seccion
        titulo="Restaurar desde un respaldo"
        descripcion="Recupera tu informacion desde un archivo JSON de respaldo. El Excel no sirve para esto."
      >
        <Banner tono="aviso" titulo="La importacion reemplaza todo">
          Los datos actuales de este dispositivo se sustituyen por los del archivo. Antes de
          hacerlo se descarga un respaldo de seguridad de forma automatica.
        </Banner>

        <input
          ref={entradaArchivo}
          type="file"
          accept="application/json,.json"
          className="sr-only"
          onChange={(evento) => {
            const archivo = evento.target.files?.[0];
            if (archivo !== undefined) void seleccionarArchivo(archivo);
          }}
        />

        {(importacion.fase === 'inactivo' || importacion.fase === 'error') && (
          <Button variante="secundario" onClick={() => entradaArchivo.current?.click()}>
            📂 Elegir archivo de respaldo
          </Button>
        )}

        {importacion.fase === 'analizando' && (
          <Banner tono="info" titulo="Revisando el archivo">
            Se valida su estructura y su suma de verificacion antes de modificar nada.
          </Banner>
        )}

        {importacion.fase === 'importando' && (
          <Banner tono="info" titulo="Importando">
            No cierres la aplicacion.
          </Banner>
        )}

        {importacion.fase === 'error' && (
          <Banner tono="alerta" titulo="No se pudo importar">
            <p>{importacion.mensaje}</p>
            <p className="mt-2 font-medium">Tus datos actuales no se modificaron.</p>
          </Banner>
        )}

        {importacion.fase === 'listo' && (
          <>
            <Banner tono="exito" titulo="Importacion completada">
              Se restauraron {importacion.registros} registros.
            </Banner>
            <Button variante="secundario" onClick={reiniciarImportacion}>
              Listo
            </Button>
          </>
        )}

        {importacion.fase === 'confirmando' && (
          <ResumenImportacion
            analizado={importacion.analizado}
            nombreArchivo={importacion.nombreArchivo}
            forzar={forzarImportacion}
            onForzar={setForzarImportacion}
            onConfirmar={() => {
              void confirmarImportacion(importacion.analizado);
            }}
            onCancelar={reiniciarImportacion}
          />
        )}
      </Seccion>

      <Banner tono="alerta" titulo="Nunca borres los datos de navegacion sin respaldo">
        En Ajustes de iPhone, "Borrar historial y datos de sitios" elimina toda la
        informacion de esta aplicacion. No se puede recuperar sin un archivo de respaldo.
      </Banner>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsResumen {
  analizado: RespaldoAnalizado;
  nombreArchivo: string;
  forzar: boolean;
  onForzar: (valor: boolean) => void;
  onConfirmar: () => void;
  onCancelar: () => void;
}

/** Resumen del contenido y confirmacion explicita (Req. 3.7). */
function ResumenImportacion({
  analizado,
  nombreArchivo,
  forzar,
  onForzar,
  onConfirmar,
  onCancelar,
}: PropsResumen) {
  const { resumen } = analizado;
  const conDatos = resumen.conteos.filter((c) => c.registros > 0 && c.tabla !== 'settings');
  const bloqueado = resumen.bloqueaImportacion;

  return (
    <div className="space-y-3">
      <Card>
        <h3 className="mb-2 font-semibold text-texto">Contenido del archivo</h3>
        <p className="mb-3 break-all text-xs text-texto-tenue">{nombreArchivo}</p>

        <dl className="divide-y divide-borde">
          <Dato etiqueta="Registros en total" valor={String(resumen.totalRegistros)} />
          {resumen.rangoFechas !== null && (
            <Dato
              etiqueta="Periodo que abarca"
              valor={`${resumen.rangoFechas.desde} a ${resumen.rangoFechas.hasta}`}
            />
          )}
          <Dato
            etiqueta="Verificacion"
            valor={resumen.checksumValido ? '✅ Correcta' : '⚠️ No coincide'}
          />
          <Dato
            etiqueta="Origen"
            valor={resumen.device === 'standalone' ? 'App instalada' : 'Navegador'}
          />
        </dl>

        {conDatos.length > 0 && (
          <ul className="mt-3 space-y-1 border-t border-borde pt-3 text-sm">
            {conDatos.map((c) => (
              <li key={c.tabla} className="flex justify-between gap-4">
                <span className="text-texto-suave">{c.etiqueta}</span>
                <span className="font-medium text-texto">{c.registros}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {resumen.advertencias.length > 0 && (
        <Banner
          tono={bloqueado ? 'alerta' : 'aviso'}
          titulo={bloqueado ? 'El archivo no paso la verificacion' : 'Revisa estas advertencias'}
        >
          <ul className="list-disc space-y-1 pl-4">
            {resumen.advertencias.map((advertencia) => (
              <li key={advertencia}>{advertencia}</li>
            ))}
          </ul>
        </Banner>
      )}

      {bloqueado && (
        <Card className="border-alerta/40 bg-alerta-suave">
          <p className="text-sm text-texto">
            La importacion esta bloqueada porque el archivo no coincide con su suma de
            verificacion: se modifico o se corrompio.
          </p>
          <p className="mt-2 text-sm text-texto">
            Si este es tu <strong>unico respaldo</strong>, puedes intentar recuperarlo de
            todas formas. Algunos datos podrian venir incompletos o alterados.
          </p>
          <label className="mt-3 flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={forzar}
              onChange={(evento) => onForzar(evento.target.checked)}
              className="mt-0.5 size-5 shrink-0"
            />
            <span>Entiendo el riesgo y quiero importarlo de todas formas</span>
          </label>
        </Card>
      )}

      <div className="space-y-2">
        <Button
          variante={bloqueado ? 'peligro' : 'primario'}
          tamano="grande"
          disabled={bloqueado && !forzar}
          onClick={onConfirmar}
        >
          {bloqueado ? 'Importar aun asi' : 'Reemplazar mis datos con este respaldo'}
        </Button>
        <Button variante="secundario" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
